import { Ruler, Scale, Sparkles } from "lucide-react";

import type { IngestionSession } from "@/api/types";
import { Field, Input } from "@/components/Input";
import { SourceTag } from "./ConfidenceIndicator";

/**
 * Le poids, les dimensions et le conditionnement lus sur l'étiquette (PM-03 §5).
 *
 * N'apparaît que si quelque chose a été lu. Un bloc de six champs vides sur
 * chaque article ralentirait le mode Blitz sans rien apporter : la plupart des
 * étiquettes ne portent pas de poids, et une fiche produit se complète très
 * bien plus tard, au calme.
 *
 * Les estimations visuelles sont montrées à part, et volontairement pas
 * pré-remplies. Un modèle qui regarde une photo de disjoncteur et dit « environ
 * 2 kg » ne pèse rien ; accepter ça d'un clic mettrait dans la base un chiffre
 * que plus personne ne pourrait distinguer d'une pesée. Si l'opérateur veut le
 * chiffre, il le tape — et c'est alors lui qui l'affirme.
 */

const PHYSICAL_FIELDS: { name: string; label: string; hint?: string }[] = [
  { name: "net_weight_mg", label: "Poids net (mg)" },
  { name: "length_mm", label: "Longueur (mm)" },
  { name: "width_mm", label: "Largeur (mm)" },
  { name: "height_mm", label: "Hauteur (mm)" },
  { name: "package.name", label: "Conditionnement" },
  { name: "package.quantity", label: "Contenu du conditionnement" },
];

const ESTIMATE_LABELS: Record<string, string> = {
  net_weight_mg: "Poids net",
  length_mm: "Longueur",
  width_mm: "Largeur",
  height_mm: "Hauteur",
};

interface VisualEstimate {
  field_name: string;
  value: string;
  provider?: string;
  reason?: string;
}

export function PhysicalProposals({
  session,
  draft,
  onChange,
}: {
  session: IngestionSession;
  draft: Record<string, string>;
  onChange: (name: string, value: string) => void;
}) {
  const analysis = (session.metadata_json?.analysis ?? {}) as {
    visual_estimates?: VisualEstimate[];
  };
  const estimates = analysis.visual_estimates ?? [];
  const present = PHYSICAL_FIELDS.filter(
    (entry) =>
      session.proposals.some((proposal) => proposal.field_name === entry.name) ||
      draft[entry.name],
  );

  if (present.length === 0 && estimates.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <p className="flex items-center gap-2 text-[13px] font-medium text-ink-muted">
        <Scale className="size-3.5" aria-hidden />
        Lu sur l'emballage
      </p>

      {present.map((entry) => {
        const proposal = session.proposals.find((p) => p.field_name === entry.name);
        return (
          <Field key={entry.name} label={entry.label} htmlFor={`field-${entry.name}`}>
            <div className="flex items-center gap-2">
              <Input
                id={`field-${entry.name}`}
                value={draft[entry.name] ?? ""}
                onChange={(event) => onChange(entry.name, event.target.value)}
              />
              {proposal && <SourceTag source={proposal.source} provider={proposal.provider} />}
            </div>
          </Field>
        );
      })}

      {estimates.length > 0 && (
        <div className="flex flex-col gap-1 rounded-md border border-border bg-surface-sunken px-3 py-2">
          <p className="flex items-center gap-2 text-[12px] font-medium text-ink-muted">
            <Sparkles className="size-3.5" aria-hidden />
            Estimations du modèle — non enregistrées
          </p>
          <ul className="flex flex-col gap-0.5">
            {estimates.map((estimate) => (
              <li
                key={`${estimate.field_name}-${estimate.value}`}
                className="flex items-center justify-between gap-2 text-[12px]"
              >
                <span className="text-ink-subtle">
                  {ESTIMATE_LABELS[estimate.field_name] ?? estimate.field_name}
                </span>
                <span className="flex items-center gap-2">
                  <Ruler className="size-3 text-ink-subtle" aria-hidden />
                  <span className="font-mono text-ink">{estimate.value}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-ink-subtle">
            Le modèle n'a lu aucun chiffre sur la photo : c'est une impression, pas une mesure.
            Saisissez la valeur vous-même si vous la connaissez.
          </p>
        </div>
      )}
    </div>
  );
}
